import { Form } from 'antd';
import React, { FunctionComponent, useState } from 'react';
import styled from 'styled-components';
import { Color } from '../../../config/Color';
import { isValidUsResidentialAddress, MBI_REGEX } from '../../../util/MedicareAccess';
import { medicareCardSample } from '../../assets/Images';
import { FlowContentBody, QuestionWrapper } from '../../styles/Containers';
import { ErrorText } from '../../styles/ErrorMessage';
import { TextInput } from '../../styles/TextInput';
import { BodySM, BodyXS, HeroTitle } from '../../styles/Typography';
import { LayoutActions } from './LayoutActions';

export type FindingYourDetailsConfig = {
  title: string;
  imageAlt: string;
};

export type MedicareMbiLayoutConfig = {
  title: string;
  subtitle?: string;
  medicareSectionTitle: string;
  personalSectionTitle: string;
  mbiLabel: string;
  mbiPlaceholder?: string;
  mbiHelperText?: string;
  mbiTooltip?: string;
  givenNameLabel: string;
  givenNamePlaceholder?: string;
  familyNameLabel: string;
  familyNamePlaceholder?: string;
  addressLabel: string;
  addressPlaceholder?: string;
  addressHelperText?: string;
  primaryButtonTitle: string;
  findingYourDetails?: FindingYourDetailsConfig;
  prefill?: {
    givenName?: string;
    familyName?: string;
    residentialAddress?: string;
    mbi?: string;
  };
};

export type MedicareMbiSubmission = {
  givenName: string;
  familyName: string;
  residentialAddress: string;
  mbi: string;
};

/**
 * MBI numbers are formatted as `CANN-ANN-AANN` (4-3-4 alphanumerics with two
 * hyphens). We strip anything the user typed back to alphanumerics, cap at 11
 * characters, then re-insert hyphens so the input stays canonically formatted.
 */
function formatMbi(raw: string): string {
  const cleaned = raw
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '')
    .slice(0, 11);
  const parts = [cleaned.slice(0, 4), cleaned.slice(4, 7), cleaned.slice(7, 11)].filter(
    (part) => part.length > 0
  );
  return parts.join('-');
}

type Props = MedicareMbiLayoutConfig & {
  onPressPrimaryButton: (submission: MedicareMbiSubmission) => void;
};

export const MedicareMbiLayout: FunctionComponent<Props> = (props) => {
  const [givenName, setGivenName] = useState(props.prefill?.givenName ?? '');
  const [familyName, setFamilyName] = useState(props.prefill?.familyName ?? '');
  const [residentialAddress, setResidentialAddress] = useState(
    props.prefill?.residentialAddress ?? ''
  );
  const [mbi, setMbi] = useState(formatMbi(props.prefill?.mbi ?? ''));
  // Only set after blur, so we don't surface an error mid-typing.
  const [showInvalid, setShowInvalid] = useState<boolean>(false);
  const [showAddressInvalid, setShowAddressInvalid] = useState<boolean>(false);

  // MBI_REGEX matches the canonical no-dash form; display has 4-3-4 hyphens.
  const rawMbi = mbi.replace(/-/g, '');
  const isMbiValid: boolean = MBI_REGEX.test(rawMbi);
  const isAddressValid: boolean = isValidUsResidentialAddress(residentialAddress);
  const canSubmit =
    givenName.trim().length > 0 && familyName.trim().length > 0 && isAddressValid && isMbiValid;

  function onMbiBlur(): void {
    setShowInvalid(rawMbi.length > 0 && !isMbiValid);
  }

  function onMbiChange(value: string): void {
    setMbi(formatMbi(value));
    // Clear any prior error while the user is still editing; revalidate on blur.
    if (showInvalid === true) setShowInvalid(false);
  }

  function onAddressBlur(): void {
    setShowAddressInvalid(residentialAddress.trim().length > 0 && !isAddressValid);
  }

  function onAddressChange(value: string): void {
    setResidentialAddress(value);
    if (showAddressInvalid === true) setShowAddressInvalid(false);
  }

  function onPrimaryPress(): void {
    if (!canSubmit) return;
    props.onPressPrimaryButton({
      givenName: givenName.trim(),
      familyName: familyName.trim(),
      residentialAddress: residentialAddress.trim(),
      mbi: rawMbi,
    });
  }

  return (
    <Form>
      <QuestionWrapper>
        <FlowContentBody>
          <HeroTitle data-test="surveyQuestion">{props.title}</HeroTitle>
          {props.subtitle !== undefined && <Subtitle>{props.subtitle}</Subtitle>}

          <SectionTitle>{props.medicareSectionTitle}</SectionTitle>
          <FieldLabel>
            {props.mbiLabel}
            {props.mbiTooltip !== undefined && <InfoIcon title={props.mbiTooltip}>ⓘ</InfoIcon>}
          </FieldLabel>
          <Form.Item>
            <TextInput
              type="text"
              autoCapitalize="characters"
              value={mbi}
              placeholder={props.mbiPlaceholder}
              aria-label={props.mbiLabel}
              data-testid="medicare-mbi-number"
              onChange={({ target: { value } }) => onMbiChange(value)}
              onBlur={onMbiBlur}
            />
          </Form.Item>
          {props.mbiHelperText !== undefined && <HelperText>{props.mbiHelperText}</HelperText>}
          {showInvalid === true && (
            <ErrorText data-testid="medicare-mbi-invalid">
              That doesn’t look like a valid MBI number. Please check the format on your card (for
              example, 1EG4-TE5-MK72) and try again.
            </ErrorText>
          )}

          <SectionTitle>{props.personalSectionTitle}</SectionTitle>
          <LabelledTextField
            label={props.givenNameLabel}
            autoComplete="given-name"
            value={givenName}
            placeholder={props.givenNamePlaceholder}
            testId="medicare-given-name"
            onChange={setGivenName}
          />
          <LabelledTextField
            label={props.familyNameLabel}
            autoComplete="family-name"
            value={familyName}
            placeholder={props.familyNamePlaceholder}
            testId="medicare-family-name"
            onChange={setFamilyName}
          />
          <LabelledTextField
            label={props.addressLabel}
            autoComplete="street-address"
            value={residentialAddress}
            placeholder={props.addressPlaceholder}
            testId="medicare-residential-address"
            onChange={onAddressChange}
            onBlur={onAddressBlur}
            errorText={
              showAddressInvalid === true
                ? 'Please enter a full address including street number and ZIP code.'
                : undefined
            }
          />
          {props.addressHelperText !== undefined && showAddressInvalid === false && (
            <HelperText>{props.addressHelperText}</HelperText>
          )}

          {props.findingYourDetails !== undefined && (
            <FindingYourDetailsCard config={props.findingYourDetails} />
          )}
        </FlowContentBody>
        <LayoutActions
          primaryButtonTitle={props.primaryButtonTitle}
          onPressPrimaryButton={onPrimaryPress}
          primaryButtonDisabled={!canSubmit}
        />
      </QuestionWrapper>
    </Form>
  );
};

type LabelledTextFieldProps = {
  label: string;
  autoComplete: string;
  value: string;
  placeholder?: string;
  testId: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  errorText?: string;
};

const LabelledTextField: FunctionComponent<LabelledTextFieldProps> = (props) => (
  <>
    <FieldLabel>{props.label}</FieldLabel>
    <Form.Item>
      <TextInput
        type="text"
        autoComplete={props.autoComplete}
        value={props.value}
        placeholder={props.placeholder}
        aria-label={props.label}
        data-testid={props.testId}
        onChange={({ target: { value } }) => props.onChange(value)}
        onBlur={props.onBlur}
      />
    </Form.Item>
    {props.errorText !== undefined && (
      <ErrorText data-testid={`${props.testId}-invalid`}>{props.errorText}</ErrorText>
    )}
  </>
);

type FindingYourDetailsCardProps = { config: FindingYourDetailsConfig };

const FindingYourDetailsCard: FunctionComponent<FindingYourDetailsCardProps> = (props) => (
  <DetailsCard>
    <SectionTitle>{props.config.title}</SectionTitle>
    <CardImage src={medicareCardSample} alt={props.config.imageAlt} />
  </DetailsCard>
);

const SectionTitle = styled(BodySM)`
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  margin-top: ${({ theme }) => theme.spacing._24px};
  margin-bottom: ${({ theme }) => theme.spacing._8px};
`;

const FieldLabel = styled(BodySM)`
  margin-bottom: ${({ theme }) => theme.spacing._8px};
`;

const Subtitle = styled(BodySM)`
  color: ${Color.content.muted};
`;

const InfoIcon = styled.span`
  margin-left: ${({ theme }) => theme.spacing._8px};
  cursor: help;
`;

const HelperText = styled(BodyXS)`
  color: ${Color.content.muted};
  margin-bottom: ${({ theme }) => theme.spacing._16px};
`;

const DetailsCard = styled.div`
  margin-top: ${({ theme }) => theme.spacing._24px};
`;

const CardImage = styled.img`
  width: 100%;
  max-width: 400px;
  margin-top: ${({ theme }) => theme.spacing._16px};
`;
